import { Inject, Options, Vue } from "vue-property-decorator";
import { TrustAPI } from "@/shared/modules/trust-api";
import { AppInsights } from "@/trust-admin-app/appInsights";
import { EventEmitter } from "events";
import store from "@/trust-admin-app/store";
import ErrorIndicator from "@/shared/components/error-indicator/index.vue";
import { GenerateNextYearTrustComplianceForm, PracticeView } from "@/shared/modules/typescript-trustapi-client/models";
import { orderBy } from "lodash";
import DropdownActions from "../dropdown-actions/index.vue";
import GenerateNextYearComplianceModel from "./generate-nextyear-compliance-model/index.vue";
import LoadingIndicator from "@/shared/components/loading-indicator/index.vue";

@Options({
  components: {
    ErrorIndicator,
    DropdownActions,
    LoadingIndicator,
    GenerateNextYearComplianceModel
  }
})
export default class PracticeTrustCompliance extends Vue {
  @Inject({ from: 'apiService', default: new TrustAPI() })
  private apiService!: TrustAPI;

  private ai = new AppInsights;

  get eventCtrl(): EventEmitter {
    return store.getters.getEventController;
  }

  private loadingContent = false;
  private errorLoadingContent = false;

  private generateComplianceFormAllowed = false;

  private tableHeadings = [
    { text: 'Compliance Year', value: 'complianceYear.name', sortable: true, sortMethod: 'desc' },
    { text: 'Progress', value: 'progress', sortable: false },
    { text: 'Non-compliant', value: 'nonCompliant', sortable: false },
    { text: 'Not required', value: 'absolution', sortable: false },
    { text: 'Action', value: 'controlled', sortable: false },
  ];

  private activeSortHeader = 1;
  private sortMethod = 'asc';
  private showGenerateNextYearConfirmation = false;
  private generateComplianceForm: GenerateNextYearTrustComplianceForm = null as any;

  private trustComplianceTableData = [];

  mounted(): void {
    window.scrollTo(0, 0);
    this.loadTableData();
    this.getGenerateComplianceFormEnabled();
  }

  private loadTableData() {
    this.loadingContent = true;
    this.apiService.ComplianceFormApi?.apiV2ComplianceFormAdminGetComplianceFormsPost(
      { skip: 0, take: 0, practiceId: this.selectedPractice.id }
    )
      .then(res => {
        this.trustComplianceTableData = res.data.data;
        this.loadingContent = false;
        this.sortTableColumn(0, true);
      })
      .catch(err => {
        this.eventCtrl.emit('show-snackbar', 'error', 'Unable to load Trust compliance data');
        this.ai.trackException('Error getting Trust compliance data: ' + err);
        this.loadingContent = false;
        this.errorLoadingContent = true;
      })

  }

  private async getGenerateComplianceFormEnabled(): Promise<void> {
    try {
      const resp: any = await this.apiService.ComplianceFormApi?.apiV2ComplianceFormAdminIsGenerateTrustComplianceFormEnablePracticeIdGet(
        this.selectedPractice.id
      );
      this.generateComplianceForm = resp.data;
      this.generateComplianceFormAllowed = resp.data.isEnabled;
    } catch (err) {
      this.eventCtrl.emit('show-snackbar', 'error', 'Unable get generate compliance form setting');
      this.ai.trackException('Error Unable get generate compliance form setting: ' + err);
    }
  }

  private async generateNextYearComplianceForm(): Promise<void> {
    try {
      if (!this.generateComplianceFormAllowed) {
        throw Error('Generate next year compliance form is disabled');
      }
      await this.apiService.ComplianceFormApi?.apiV2ComplianceFormAdminGenerateNextYearTrustComplianceFormPracticeIdGet(
        this.selectedPractice.id
      );
      this.showGenerateNextYearConfirmation = !this.showGenerateNextYearConfirmation;
      this.loadTableData();
      this.getGenerateComplianceFormEnabled();
    } catch (err) {
      this.eventCtrl.emit('show-snackbar', 'error', 'Unable to generate next year compliance form');
      this.ai.trackException('Error Unable get generate compliance form setting: ' + err);
    }
  }

  private generateNextYearComplianceFormButtonClicked(): void {
    this.showGenerateNextYearConfirmation = !this.showGenerateNextYearConfirmation;
  }

  private statusColorCSS(statusId: number) {
    switch (statusId) {
      case 1:
        // In progress
        return "color: #D7B301";
      case 2:
        // Part A Resubmission required
        return "color: #638886";
      case 3:
      case 12:
        // Part B Resubmission required
        return "color: #638886";
      case 4:
        // Not yet started
        return "color: #9EA5AB";
      case 5:
      case 11:
        // Awaiting External Examiner response
        // EE: Ready to be reviewed
        return "color: #1968AB";
      case 6:
      case 13:
        // Non-compliant
        // EE: Trust Compliance not received
        return "color: #C0392B";
      case 7:
      case 14:
        // Completed
        return "color: #27AE60";
      default:
        return "color: #000";
    }
  }

  private sortTableColumn(index: number, sortable: boolean) {
    if (sortable) {
      if (this.tableHeadings[index].sortable) {
        this.activeSortHeader = index;
        this.sortMethod = this.sortMethod === 'desc' ? 'asc' : 'desc';
        this.tableHeadings[index].sortMethod = this.sortMethod;
        // console.log(this.tableHeadings[index].sortMethod );
        if (this.trustComplianceTableData.length > 0) {
          const orderByResults: any = orderBy(
            this.trustComplianceTableData,
            this.tableHeadings[index].value,
            [this.sortMethod as any]
          );
          this.trustComplianceTableData = orderByResults;
        }
      }
    }
  }

  get selectedPractice(): PracticeView {
    return store.getters.getSelectedPractice;
  }

  private getAbsolution(tc: any): string {
    let absolution = '-';

    const part_a = tc.isAbsolvedPartA ? 'Part A' : '';
    const part_b = tc.isAbsolvedPartB ? ', Part B' : '';
    const eer = tc.isAbsolvedEeReport ? ', EE Report' : '';

    if (part_a === '' && part_b === '' && eer === '') {
      return absolution;
    } else {
      absolution = part_a + part_b + eer;
    }
    return absolution.replace(/^,|,$/g, '');
  }

  private getNonCompliant(tc: any): string {
    let nonCompliant = '-';
    const part_a = tc.isNonCompliantPartAComputed ? 'Part A' : '';
    const part_b = tc.isNonCompliantPartBComputed ? ', Part B' : '';
    const eer = tc.isNonCompliantEeReportComputed ? ', EE Report' : '';

    if (part_a === '' && part_b === '' && eer === '') {
      return nonCompliant;
    } else {
      nonCompliant = part_a + part_b + eer;
    }
    return nonCompliant.replace(/^,|,$/g, '');

  }

  private async reloadData() {
    this.sortMethod = 'asc';
    this.loadTableData();
  }

}

